import { camelCase } from "lodash-es";
import { IntlShape } from "react-intl";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import HasSourceObject from "@mapmycustomers/shared/types/base/HasSourceObject";
import { EntityTypesSupportingCustomSourceObject } from "@mapmycustomers/shared/types/entity";
import SourceObjectValue from "@mapmycustomers/shared/types/SourceObjectValue";
import {
  isEntityTypeSupportingCustomSourceObject,
  isNotEmpty,
} from "@mapmycustomers/shared/util/assert";

import getEntityTypeDisplayName from "@app/util/ui/getEntityTypeDisplayName";

// The only copy of the gate: an org "uses" source objects once it has more than the standard one
export const hasCustomSourceObjects = (values: SourceObjectValue[]) =>
  values.some((item) => !item.isStandard);

export const getSourceObjectLabel = (
  intl: IntlShape | undefined,
  entityType: EntityTypesSupportingCustomSourceObject,
  values: SourceObjectValue[],
  key: HasSourceObject["sourceObject"] | undefined
): string | undefined => {
  if (key === undefined) {
    return undefined;
  }
  const match = values.find((item) => item.key === key);
  if (key === null) {
    // null IS the standard object per HasSourceObject, whatever the org's list holds — an org with
    // no integration reports no values at all, and its variants still need names.
    // Product wording is Company / Person, not the integration's label (e.g. Account / Contact).
    return intl ? getEntityTypeDisplayName(intl, entityType) : match?.label;
  }
  return match?.label ?? key;
};

// The reserved ICU key for the standard object. null IS the standard object, and ICU cannot switch
// on null, so every subtype message spells it this way. Never a real source object key.
export const STANDARD_SOURCE_OBJECT_INTL_KEY = "standard";

// ICU select arms cannot hold a dash but source object keys can, so every key is camelCased.
// An absent key deliberately reads as standard here — a plausible title beats an empty one.
export const getSourceObjectIntlKey = (key: HasSourceObject["sourceObject"] | undefined): string =>
  isNotEmpty(key) ? camelCase(key) : STANDARD_SOURCE_OBJECT_INTL_KEY;

// A type alias, not an interface: react-intl's `values` parameter is Record<string, ...>, and only
// a type alias gets the implicit index signature that makes it assignable without a spread.
export type SourceObjectMessageArgs = {
  label: string | undefined;
  sourceObject: string;
};

// Both arguments together: a select on sourceObject whose other arm lacks a label renders "".
// intl is optional for sagas — i18nService.getIntl() is undefined until the first locale loads.
export const getSourceObjectMessageArgs = (
  intl: IntlShape | undefined,
  entityType: EntityType | undefined,
  values: SourceObjectValue[],
  key: HasSourceObject["sourceObject"] | undefined
): SourceObjectMessageArgs => ({
  label: isEntityTypeSupportingCustomSourceObject(entityType)
    ? getSourceObjectLabel(intl, entityType, values, key)
    : undefined,
  sourceObject: getSourceObjectIntlKey(key),
});

// Keys the web writes its own ICU arms for, so a subtype name inflects per language.
// Per entity type: a Company lead and a Person lead need not be one noun. Raw keys, not arms.
export const KNOWN_SOURCE_OBJECTS: Readonly<
  Record<EntityTypesSupportingCustomSourceObject, readonly string[]>
> = {
  [EntityType.COMPANY]: ["leadCompanies", "siteCompanies", "projectCompanies", "shopCompanies"],
  [EntityType.PERSON]: ["partnerPeople", "leadPeople", "staffMemberPeople", "leadPersonnel"],
};
